import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartPulse, faMapLocationDot, faTableColumns, faTable } from '@fortawesome/free-solid-svg-icons';
import MultiLineChart from '../graphs/MultiLineChart';
import DayRangePicker from './DayRangePicker';
import { apiFetch } from '../../api/apiClient';
import { normalizeHourGraphPayload } from '../../api/util';
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from '../../utils/dateTime';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

// Line colors for the four diagnostic series.
const SERIES_COLORS = {
  sel: '#008900',   // green
  vAc: '#D07F15',   // orange
};

const MISSING_SEL_COLOR = '#c5c5c5';

// Anchor colors for the 11-stop gradient band used to color powerblock tiles
// by SEL AVG: stop 0 = green (80), stop 5 = yellow (90), stop 10 = red (100+).
const GRADIENT_ANCHORS = {
  green: '#008900',
  yellow: '#A48C00',
  red: '#9F0000',
};

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbToHex = (rgb) =>
  `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

const mixColors = (from, to, t) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * t));
};

const GRADIENT_STOPS = Array.from({ length: 11 }, (_, i) =>
  i <= 5
    ? mixColors(GRADIENT_ANCHORS.green, GRADIENT_ANCHORS.yellow, i / 5)
    : mixColors(GRADIENT_ANCHORS.yellow, GRADIENT_ANCHORS.red, (i - 5) / 5)
);

const SEL_AVG_GREEN = 80;
const SEL_AVG_RED = 100;

const selAvgToColor = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return MISSING_SEL_COLOR;
  const clamped = Math.max(SEL_AVG_GREEN, Math.min(SEL_AVG_RED, value));
  const index = Math.round(((clamped - SEL_AVG_GREEN) / (SEL_AVG_RED - SEL_AVG_GREEN)) * 10);
  return GRADIENT_STOPS[index];
};

// Stable references for the chart's Y axis. Module scope keeps their identity
// stable across renders so they don't re-trigger the chart's draw effect.
const CHART_DOMAIN = [80, 100];
const CHART_TICK_VALUES = [80, 85, 90, 95, 100];

const TRANSFORMER_IDS = ['1', '2', '3', '4'];

const powerblockHealthMetricsUrl = (portfolioName, powerblockId) =>
  `/api/metrics/portfolio/${portfolioName}/sector/powerblocks/health-metrics/${encodeURIComponent(powerblockId)}`;

const filterPointsByRange = (points, range) => {
  if (!range?.start || !range?.end) return points;
  const start = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
  const end = range.end;
  return points.filter(
    (point) =>
      point.date instanceof Date &&
      !Number.isNaN(point.date) &&
      point.date >= start &&
      point.date <= end
  );
};

const healthMetricSeries = (healthData, range) => {
  if (!healthData || !range?.start || !range?.end) {
    return { sel: [], vAcv: [], cmd: [], fb: [], vDcv: [] };
  }

  return {
    sel: filterPointsByRange(normalizeHourGraphPayload(healthData.sel), range),
    vAcv: filterPointsByRange(normalizeHourGraphPayload(healthData.v_acv ?? healthData.vAcv), range),
    cmd: filterPointsByRange(normalizeHourGraphPayload(healthData.cmd), range),
    fb: filterPointsByRange(normalizeHourGraphPayload(healthData.fb), range),
    vDcv: filterPointsByRange(normalizeHourGraphPayload(healthData.v_dcv ?? healthData.vDcv), range),
  };
};

const EMPTY_DETAIL_STATS = {
  sel: { min: null, max: null, avg: null },
  vAc: { min: null, max: null, avg: null },
};

const minMaxAvgFromPoints = (points) => {
  const values = (points ?? [])
    .map((point) => point?.value)
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .map(Number);
  if (values.length === 0) {
    return { min: null, max: null, avg: null };
  }
  const sum = values.reduce((acc, value) => acc + value, 0);
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    avg: sum / values.length,
  };
};

const detailStatsFromPowerblock = (pb, vAcPoints) => {
  if (!pb) return EMPTY_DETAIL_STATS;

  return {
    sel: { min: pb.sel_min, max: pb.sel_max, avg: pb.sel_avg },
    vAc: minMaxAvgFromPoints(vAcPoints),
  };
};

// Sort powerblock ids alphanumerically: letter prefix first, then numeric
// suffix (e.g. A1..A20, B1..B108, D1..D49, E1..E8, SB1).
const comparePowerblockIds = (a, b) => {
  const parse = (id) => {
    const match = String(id).match(/^([A-Za-z]*)(\d*)$/);
    return { prefix: match?.[1] ?? String(id), number: match?.[2] ? Number(match[2]) : 0 };
  };
  const pa = parse(a);
  const pb = parse(b);
  if (pa.prefix !== pb.prefix) {
    return pa.prefix.localeCompare(pb.prefix);
  }
  return pa.number - pb.number;
};

const fmt = (value, digits = 2) => (value != null ? value.toFixed(digits) : '--');

const fmtTime = (date, dateFormatter) =>
  dateFormatter.formatDate(date, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function numericColumn(title, digits = 2, { grayEmpty = false } = {}) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? 0;
      }

      return fmt(cellData, digits);
    },
    createdCell: grayEmpty
      ? (cell, cellData) => {
          if (cellData == null || cellData === '') {
            cell.style.backgroundColor = MISSING_SEL_COLOR;
          }
        }
      : undefined,
  };
}

function DiagnosticPowerBlocks({ portfolioName, onTooltip, timeZone = DEFAULT_TIME_ZONE }) {
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(timeZone),
    [timeZone],
  );
  const [t1xfmrView, setT1xfmrView] = useState(true);
  const [selectedXfmr, setSelectedXfmr] = useState('1');
  const [data, setData] = useState(null);
  const [healthMetricsCache, setHealthMetricsCache] = useState({});
  const [activePB, setActivePB] = useState(0);
  const [activePBData, setActivePBData] = useState(null);
  const [tablePB, setTablePB] = useState(0);
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);

  // Day range that windows the chart, details and table. Defaults to the
  // previous five days (today plus the prior four), at whole-day boundaries.
  const [chartRange, setChartRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
    };
  });

  useEffect(() => {
    if (data && Object.keys(data).includes(activePB)) {
      setActivePBData(data[activePB]);
    } else {
      setActivePBData(null);
    }
  }, [data, activePB]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        let result;

        if (selectedXfmr === 'all') {
          const responses = await Promise.all(
            TRANSFORMER_IDS.map((id) =>
              apiFetch(`/api/metrics/portfolio/${portfolioName}/sector/powerblocks/${id}`).catch((apiError) => {
                console.warn(
                  `API fetch failed for /api/metrics/portfolio/${portfolioName}/sector/powerblocks/${id}:`,
                  apiError
                );
                return {};
              })
            )
          );
          const merged = responses.reduce(
            (acc, res) => (res && typeof res === 'object' && !res.error ? { ...acc, ...res } : acc),
            {}
          );
          result = Object.keys(merged)
            .sort(comparePowerblockIds)
            .reduce((acc, key) => {
              acc[key] = merged[key];
              return acc;
            }, {});
        } else {
          result = await apiFetch(`/api/metrics/portfolio/${portfolioName}/sector/powerblocks/${selectedXfmr}`);
        }

        if (!cancelled) {
          setData(result);
          setHealthMetricsCache({});

          const keys = Object.keys(result).sort(comparePowerblockIds);
          if (keys.length > 0) {
            setActivePB(keys[0]);
            setTablePB(keys[0]);
          }
        }
      } catch (apiError) {
        console.warn(
          `API fetch failed for /api/metrics/portfolio/${portfolioName}/sector/powerblocks (xfmr ${selectedXfmr}):`,
          apiError
        );
        if (!cancelled) {
          setData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedXfmr, portfolioName]);

  // Fetch health metrics on demand when the active or table powerblock changes.
  useEffect(() => {
    const powerblockIdsToFetch = [...new Set([activePB, tablePB].filter((id) => id && id !== 0))];
    const missingIds = powerblockIdsToFetch.filter((pbId) => !healthMetricsCache[pbId]);
    if (missingIds.length === 0) return undefined;

    let cancelled = false;

    (async () => {
      for (const pbId of missingIds) {
        if (cancelled) break;

        try {
          const result = await apiFetch(powerblockHealthMetricsUrl(portfolioName, pbId));
          if (cancelled || !result || typeof result !== 'object' || result.error) continue;

          setHealthMetricsCache((prev) => (prev[pbId] ? prev : { ...prev, [pbId]: result }));
        } catch (apiError) {
          console.warn(`API fetch failed for ${powerblockHealthMetricsUrl(portfolioName, pbId)}:`, apiError);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activePB, tablePB, portfolioName, healthMetricsCache]);

  const toggleT1xfmrView = () => {
    setT1xfmrView((prev) => !prev);
  };

  // Powerblock ids belonging to the current transformer selection, ordered
  // alphanumerically. Drives both the heatmap tiles and the table's powerblock
  // dropdown.
  const powerblockIds = useMemo(
    () => (data ? Object.keys(data).sort(comparePowerblockIds) : []),
    [data]
  );

  // Keep the table's selected powerblock valid as the transformer (and thus the
  // available powerblocks) changes.
  useEffect(() => {
    if (powerblockIds.length === 0) return;
    if (!powerblockIds.includes(tablePB)) {
      setTablePB(powerblockIds[0]);
    }
  }, [powerblockIds, tablePB]);

  // Per-powerblock SEL AVG from the sector powerblocks list; drives tile colors
  // immediately on XFMR load without waiting for per-block health-metric fetches.
  const pbSelAvgs = useMemo(() => {
    const result = {};
    powerblockIds.forEach((id) => {
      const avg = data?.[id]?.sel_avg;
      result[id] = avg != null ? Number(avg) : null;
    });
    return result;
  }, [powerblockIds, data]);

  const activeSeries = useMemo(
    () => healthMetricSeries(healthMetricsCache[activePB], chartRange),
    [healthMetricsCache, activePB, chartRange]
  );

  const chartSeries = useMemo(
    () => [
      { data: activeSeries.sel, color: SERIES_COLORS.sel, label: 'SEL' },
      { data: activeSeries.vAcv, color: SERIES_COLORS.vAc, label: 'V AC' },
    ],
    [activeSeries]
  );

  // Details-panel SEL from sector list; V AC from health-metrics series.
  const detailStats = useMemo(
    () => detailStatsFromPowerblock(activePBData, activeSeries.vAcv),
    [activePBData, activeSeries.vAcv]
  );

  // One table row per 5-minute interval for the table's selected powerblock.
  // SEL / V AC V / CMD / FB / V DC V come from the health-metrics API.
  const tableSeries = useMemo(
    () => healthMetricSeries(healthMetricsCache[tablePB], chartRange),
    [healthMetricsCache, tablePB, chartRange]
  );
  const tableRows = useMemo(() => {
    const { sel, vAcv, cmd, fb, vDcv } = tableSeries;
    if (sel.length === 0) return [];
    return sel.map((point, i) => ({
      date: point.date,
      sel: point.value,
      vAcv: vAcv[i]?.value,
      cmd: cmd[i]?.value,
      fb: fb[i]?.value,
      vDcv: vDcv[i]?.value,
    }));
  }, [tableSeries]);

  useEffect(() => {
    if (t1xfmrView) {
      resetTableHost(tableHostRef, dataTableRef);
      return undefined;
    }

    const host = tableHostRef.current;
    if (!host || tableRows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped w-100 diagnostic-powerblocks-table';
    table.style.width = '100%';
    host.appendChild(table);

    const columns = [
      {
        title: 'TIME',
        render: (cellData, type) => {
          if (type === 'sort' || type === 'type') {
            return cellData;
          }

          return fmtTime(new Date(cellData), dateFormatter);
        },
      },
      numericColumn('SEL AVG', 2, { grayEmpty: true }),
      numericColumn('V AC V AVG', 0),
      numericColumn('CMD AVG'),
      numericColumn('FB AVG'),
      numericColumn('V DC V AVG', 0),
    ];

    const data = tableRows.map((row) => [
      row.date.getTime(),
      row.sel,
      row.vAcv,
      row.cmd,
      row.fb,
      row.vDcv,
    ]);

    dataTableRef.current = new DataTable(table, {
      data,
      columns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      autoWidth: false,
      initComplete: () => {
        host.querySelectorAll('table.diagnostic-powerblocks-table').forEach((el) => {
          el.style.width = '100%';
        });
        host.querySelectorAll('thead th').forEach((th) => {
          th.style.removeProperty('width');
          th.style.removeProperty('min-width');
          th.style.removeProperty('max-width');
        });
        dataTableRef.current?.columns.adjust().draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableRows, t1xfmrView, tablePB, dateFormatter]);

  useEffect(() => () => {
    resetTableHost(tableHostRef, dataTableRef);
  }, []);

  const selectedStatusColor = selAvgToColor(pbSelAvgs[activePB]);
  const selectedLabel = activePB && activePB !== 0 ? activePB : '--';

  const chartLabels = (
    <span>
      <span style={{ color: SERIES_COLORS.sel }}>SEL</span>
      <span style={{ color: '#000' }}>, </span>
      <span style={{ color: SERIES_COLORS.vAc }}>V AC</span>
    </span>
  );

  if (data === undefined) {
    return null;
  }

  return (
    <div id="plantpowerblocks" className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3">
        <div className="left d-flex gap-3 align-items-center">
          <span><FontAwesomeIcon icon={faHeartPulse} /> POWERBLOCKS</span>
          <select
            className="form-select form-select-sm w-auto"
            value={selectedXfmr}
            onChange={(e) => setSelectedXfmr(e.target.value)}
          >
            {TRANSFORMER_IDS.map((id) => (
              <option key={id} value={id}>XFMR {id}</option>
            ))}
            <option value="all">All Transformers</option>
          </select>
          {!t1xfmrView && (
            <select
              className="form-select form-select-sm w-auto"
              value={tablePB}
              onChange={(e) => {
                const nextPb = e.target.value;
                setTablePB(nextPb);
                setActivePB(nextPb);
              }}
            >
              {powerblockIds.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          )}
          <DayRangePicker range={chartRange} setRange={setChartRange} />
        </div>
        <div className="right d-flex align-items-stretch gap-2">
          <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button> <button onClick={toggleT1xfmrView}><FontAwesomeIcon icon={t1xfmrView ? faTableColumns : faTable} /></button>
        </div>
      </div>
      <div className={`body gap-5 justify-content-between pt-3 align-items-start ${ t1xfmrView ? 'd-flex' : 'd-none' }`}>
        <div className="col-8">
          <MultiLineChart
            unit=""
            tooltipUnit=""
            domain={CHART_DOMAIN}
            tickValues={CHART_TICK_VALUES}
            scaleYToDataExtent
            showPoints={false}
            enableZoom
            wrapperClassName="combined mb-3"
            startDate={chartRange?.start}
            endDate={chartRange?.end}
            headerLeft={
              <>
                <strong style={{ color: '#000' }}>Powerblock:</strong>{' '}
                <strong style={{ color: selectedStatusColor }}>{selectedLabel}</strong>
              </>
            }
            headerRight={chartLabels}
            series={chartSeries}
            onTooltip={onTooltip}
            timeZone={timeZone}
          />
          <div className="powerblock-gradient-legend d-flex align-items-center gap-2 mt-3 mb-4">
            <span className="small">{SEL_AVG_GREEN}</span>
            <div
              className="flex-grow-1 d-flex align-items-center justify-content-center px-2"
              style={{
                minHeight: '28px',
                borderRadius: '6px',
                background: `linear-gradient(to right, ${GRADIENT_STOPS.join(', ')})`,
              }}
            >
              <span
                className="small text-white mb-0 text-center"
                style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.65)' }}
              >
                SEL AVG
              </span>
            </div>
            <span className="small">{SEL_AVG_RED}</span>
          </div>
          <ul className="powerblocks">
            {powerblockIds.length > 0 &&
              powerblockIds.map((id, index) => {
                const tileColor = selAvgToColor(pbSelAvgs[id]);
                const isSelected = activePB === id;
                return (
                  <li
                    key={index}
                    className={isSelected ? 'selected' : ''}
                    style={
                      isSelected
                        ? { background: '#fff', border: `1px solid ${tileColor}`, color: tileColor }
                        : { background: tileColor }
                    }
                  >
                    <Link onClick={() => setActivePB(id)} className="w-100 h-100 d-flex align-items-center justify-content-center">
                      {id}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="col">
          {activePBData !== null &&
          <div className="details">
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>POWERBLOCK</strong> {activePB}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>TRANSFORMER</strong> {activePBData.transformer_id != null ? `XFMR ${activePBData.transformer_id}` : '--'}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>CIRCUITS</strong> {Array.isArray(activePBData.circuits) ? (activePBData.circuits.length > 0 ? activePBData.circuits.join(', ') : '--') : (activePBData.circuits != null && activePBData.circuits !== '' ? activePBData.circuits : '--')}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>SEL MAX</strong> {fmt(detailStats.sel.max)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>SEL MIN</strong> {fmt(detailStats.sel.min)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>SEL AVG</strong> {fmt(detailStats.sel.avg)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>V AC MAX</strong> {fmt(detailStats.vAc.max)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>V AC MIN</strong> {fmt(detailStats.vAc.min)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>V AC AVG</strong> {fmt(detailStats.vAc.avg)}</div>
            <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>Inverter Output, MW AC</strong> --</div>
          </div>}
        </div>
      </div>
      <div className={`body pt-3 ${ t1xfmrView ? 'd-none' : 'd-block' }`}>
        {tableRows.length === 0 && (
          <p className="mb-2">No data available</p>
        )}
        <div
          ref={tableHostRef}
          className="diagnostic-powerblocks-table-host"
        />
      </div>
    </div>
  );
}

export default DiagnosticPowerBlocks;
